/**
 * RegisterProgressStepper — iter1096 / iter1097
 *
 * Прогрес индикатор за целия signup flow:
 *   1/N — Регистрация (form + verify email; Google OAuth users я имат auto-done)
 *   2/N — Cycle wizard (само за жени; при `skipCycle=true` СЕ КРИЕ НАПЪЛНО)
 *   3/N — Health Assessment (/onboarding)
 *   4/N — Готов (финална celebration, 2s)
 *
 * `currentStep` използва stable ID-та (1..4) независимо дали step 2 е
 * hidden. Това пази callers-те (Register / Onboarding / AuthCallback /
 * RegisterCompleteScreen) прости — просто задават id-то на активната стъпка.
 *
 * Обратно съвместим — не изисква context, работи чисто с props.
 */
import React from "react";
import { useTheme } from "@/theme/ThemeContext";
import { useLanguage } from "@/i18n/LanguageContext";
import { Check } from "lucide-react";

const ALL_STEPS = [
  { id: 1, key: "register", label_bg: "Регистрация", label_en: "Register" },
  { id: 2, key: "cycle", label_bg: "Цикъл", label_en: "Cycle" },
  { id: 3, key: "assessment", label_bg: "Оценка", label_en: "Assessment" },
  { id: 4, key: "done", label_bg: "Готов", label_en: "Ready" },
];

/**
 * @param {1|2|3|4} currentStep — на коя стъпка е user-a. `4` = всичко done.
 * @param {boolean} [skipCycle] — напълно скрива стъпка 2 „Цикъл" (мъже
 *   или жени с вече попълнено LMP).
 * @param {"compact"|"pill"} [variant] — compact за в модала (по-малък padding).
 */
export default function RegisterProgressStepper({
  currentStep = 1,
  skipCycle = false,
  variant = "pill",
}) {
  const { isDark } = useTheme();
  const { language } = useLanguage();
  const bg = language === "bg";

  const isCompact = variant === "compact";

  // iter1097 — за мъже (skipCycle=true) стъпка „Цикъл" се крие изцяло.
  const steps = ALL_STEPS.filter((s) => !(s.id === 2 && skipCycle));

  return (
    <div
      data-testid="register-progress-stepper"
      data-skip-cycle={skipCycle || undefined}
      className={`w-full ${isCompact ? "mb-3" : "mb-5"}`}
    >
      <div className="flex items-center gap-1.5 sm:gap-2">
        {steps.map((s, idx) => {
          const isDone = currentStep > s.id;
          const isActive = currentStep === s.id;
          const isPending = currentStep < s.id;

          const bar = isDone
            ? (isDark ? "bg-emerald-500" : "bg-emerald-600")
            : isActive
            ? (isDark ? "bg-gradient-to-r from-fuchsia-500 to-purple-500" : "bg-fuchsia-600")
            : (isDark ? "bg-white/10" : "bg-slate-200");

          return (
            <React.Fragment key={s.id}>
              <div
                data-testid={`stepper-step-${s.key}`}
                data-active={isActive || undefined}
                data-done={isDone || undefined}
                className="flex-1 flex flex-col items-center gap-1 min-w-0"
              >
                <div className={`h-1.5 w-full rounded-full transition-all ${bar}`} />
                <div className={`flex items-center gap-1 text-[10px] sm:text-[11px] font-bold uppercase tracking-wider truncate ${
                  isActive
                    ? (isDark ? "text-fuchsia-300" : "text-fuchsia-700")
                    : isDone
                    ? (isDark ? "text-emerald-300" : "text-emerald-700")
                    : (isDark ? "text-slate-500" : "text-slate-400")
                }`}>
                  {isDone ? <Check className="w-3 h-3" /> : (
                    <span className={`w-4 h-4 inline-flex items-center justify-center rounded-full text-[9px] ${
                      isActive
                        ? (isDark ? "bg-fuchsia-500/20" : "bg-fuchsia-100")
                        : (isDark ? "bg-white/10" : "bg-slate-100")
                    }`}>{s.id}</span>
                  )}
                  <span className="truncate">
                    {bg ? s.label_bg : s.label_en}
                  </span>
                </div>
              </div>
              {idx < steps.length - 1 && (
                <div className={`hidden sm:block w-3 h-px ${isDark ? "bg-white/10" : "bg-slate-300"}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}
